import { createContext, useContext } from 'react';
import { TableProviderProps, Values } from '../types';

// Sometimes we need to use "any"
// eslint-disable-next-line
const TableContext = createContext<Values<any> | null>(null);

export const TableProvider = <T,>({ children, value }: TableProviderProps<T>) => {
  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
};

function useTableContext<T>() {
  const context = useContext(TableContext);
  if (context === null) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return context as Values<T>;
}

/**
 * Optional version of useTableContext that returns null instead of throwing
 * when used outside of TableProvider. Useful for components that may be
 * rendered both inside and outside of a table context.
 */
function useTableContextOptional<T>() {
  return useContext(TableContext) as Values<T> | null;
}

/**
 * Returns the resolved row height from the nearest TableProvider.
 * Falls back to 40 (default row height) when used outside a provider.
 */
function useRowHeight(): number {
  return useContext(TableContext)?.rowHeight ?? 40;
}

export { useTableContext, useTableContextOptional, useRowHeight };
